import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NonNullableFormBuilder, FormArray, FormGroup, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Subject, noop } from 'rxjs';
import { filter, map, takeUntil, take } from 'rxjs/operators';
import {
  BaseButtonSize,
  BaseCoreState,
  baseInquirySelectors,
  DefaultDeliveryTimeListItemResponse,
  DeliveryType,
  BaseModalService,
  baseInquiryActions,
  BranchItemResponse,
} from 'base';
import {
  BaseInquiryCategoriesFormInterface,
  BaseDefaultCategoriesFormInterface,
} from '../../_interfaces/base-inquiry-categories-form.interface';

@Component({
  selector: 'base-customer-service-provider-select-modal',
  templateUrl: './base-customer-service-provider-select-modal.component.html',
})
export class BaseCustomerServiceProviderSelectModalComponent implements OnInit, OnDestroy {
  @ViewChild('publishConfirmationModal', { static: true }) publishConfirmationModal: ElementRef;

  @Input() modalRef: NgbModalRef;
  @Input() isInquiryDefaultFilter = false;
  @Input() inquiryId: number;
  @Input() isOpenFromStaffRequestModal: boolean = false;

  defaultDeliveryTimeList: DefaultDeliveryTimeListItemResponse[] = [];
  form: FormGroup<BaseDefaultCategoriesFormInterface>;
  submitted = false;
  buttonSize = BaseButtonSize;
  publishConfirmationModalRef: NgbModalRef;

  private destroy$ = new Subject<boolean>();

  constructor(
    private fb: NonNullableFormBuilder,
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService
  ) {}

  get categoriesFormGroup() {
    return this.form.controls.categories as FormArray<FormGroup<BaseInquiryCategoriesFormInterface>>;
  }

  get isNoSelectedCategories() {
    return !this.categoriesFormGroup.getRawValue().some((category) => category.enabled);
  }

  ngOnInit() {
    this.initForm();
    this.getDefaultDeliveryTimeList(this.inquiryId);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getBranches(category: DeliveryType | undefined) {
    if (!category) {
      return [];
    }

    return this.defaultDeliveryTimeList.find((item) => item.category === category)?.branches || [];
  }

  publish() {
    this.publishConfirmationModalRef = this.modalService.open(
      {
        headText: 'inquiry.publish',
        bodyTemplate: this.publishConfirmationModal,
        okText: 'action.yesPublish',
        cancelText: 'action.back',
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.publishConfirmationModalRef.result.then(() => {
      this.createDeliveryTimesList();
    }, noop);
  }

  restoreDefaultSettings(event: Event) {
    event.preventDefault();

    this.form.reset();
  }

  private createDeliveryTimesList() {
    this.store.dispatch(
      baseInquiryActions.createCustomerDeliveryTimeList({
        payload: {
          inquiryId: this.inquiryId,
          body: this.getDeliveryTimeCategoriesPayload(),
          callback: () => {
            this.modalRef.close();
          },
        },
      })
    );
  }

  private getDeliveryTimeCategoriesPayload() {
    return {
      deliveryTimes: this.categoriesFormGroup.getRawValue().map((category) => ({
        deliveryTime: category.deliveryTime,
        branches: category.branches.map((item) => item.id),
        deliveryType: category.name,
      })),
    };
  }

  private initForm() {
    this.form = this.fb.group<BaseDefaultCategoriesFormInterface>({
      categories: this.fb.array<FormGroup<BaseInquiryCategoriesFormInterface>>([
        this.createCategoryFormGroup('a' as DeliveryType),
        this.createCategoryFormGroup('b' as DeliveryType),
        this.createCategoryFormGroup('c' as DeliveryType),
      ]),
      sendToAllAtOnce: this.fb.control(false),
      deliveryTimeToAll: this.fb.control({ value: 0, disabled: true }, [Validators.min(0)]),
      deliveryTimeImmediately: this.fb.control({ value: true, disabled: true }),
    });
  }

  private createCategoryFormGroup(name: DeliveryType): FormGroup<BaseInquiryCategoriesFormInterface> {
    return this.fb.group<BaseInquiryCategoriesFormInterface>({
      name: this.fb.control(name),
      enabled: this.fb.control(false),
      branches: this.fb.control([] as BranchItemResponse[]),
      deliveryTime: this.fb.control(0, [Validators.min(0)]),
      immediately: this.fb.control(true),
    });
  }

  private setInitialCategoriesForm(list: DefaultDeliveryTimeListItemResponse[]) {
    this.categoriesFormGroup.clear();

    list.forEach((item) => {
      const group = this.fb.group<BaseInquiryCategoriesFormInterface>({
        name: this.fb.control(item.category),
        enabled: this.fb.control({
          value: item.branches.length > 0,
          disabled: item.branches.length === 0,
        }),
        branches: this.fb.control(item.branches),
        deliveryTime: this.fb.control({ value: item.deliveryTime, disabled: item.branches.length === 0 }, [
          Validators.min(0),
        ]),
        immediately: this.fb.control({
          value: item.deliveryTime === 0,
          disabled: item.branches.length === 0,
        }),
      });

      this.categoriesFormGroup.push(group);
    });
  }

  private getDefaultDeliveryTimeList(inquiryId: number) {
    this.store.dispatch(
      baseInquiryActions.loadDefaultDeliveryTimeList({
        payload: inquiryId,
      })
    );

    this.store
      .select(baseInquirySelectors.getDefaultDeliveryTimeList)
      .pipe(
        filter((state) => !!state.ok),
        map((state) => state.data as DefaultDeliveryTimeListItemResponse[]),
        take(1)
      )
      .subscribe((list) => {
        this.defaultDeliveryTimeList = list;

        this.setInitialCategoriesForm(list);
        this.setupFormListeners();
      });
  }

  private setupFormListeners(): void {
    const deliveryTimeImmediateControl = this.form.controls.deliveryTimeImmediately;
    const deliveryTimeToAllControl = this.form.controls.deliveryTimeToAll;
    const sendToAllAtOnceControl = this.form.controls.sendToAllAtOnce;

    this.categoriesFormGroup.controls.forEach((categoryGroup) => {
      const immediatelyControl = categoryGroup.controls.immediately;
      const deliveryTimeControl = categoryGroup.controls.deliveryTime;
      const branchesControl = categoryGroup.controls.branches;
      const enabledControl = categoryGroup.controls.enabled;
      const categoryName = categoryGroup.controls.name;

      enabledControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((enabled) => {
        if (enabled) {
          if (deliveryTimeControl.value > 0) {
            immediatelyControl.enable({ emitEvent: false });
          } else {
            immediatelyControl.disable({ emitEvent: false });
          }

          deliveryTimeControl.enable({ emitEvent: false });

          branchesControl.setValue(this.getBranches(categoryName.value), { emitEvent: false });
        } else {
          deliveryTimeControl.disable({ emitEvent: false });
          immediatelyControl.disable({ emitEvent: false });

          branchesControl.setValue([], { emitEvent: false });
        }
      });

      branchesControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((branches) => {
        enabledControl.setValue(branches.length > 0, { emitEvent: false });

        if (branches.length > 0 && !sendToAllAtOnceControl.value) {
          deliveryTimeControl.enable({ emitEvent: false });

          if (deliveryTimeControl.value === 0) {
            immediatelyControl.disable({ emitEvent: false });
          } else {
            immediatelyControl.enable({ emitEvent: false });
          }
        } else {
          immediatelyControl.disable({ emitEvent: false });
          deliveryTimeControl.disable({ emitEvent: false });
        }
      });

      immediatelyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((isImmediately) => {
        if (isImmediately) {
          deliveryTimeControl.setValue(0, { emitEvent: false });
          immediatelyControl.disable({ emitEvent: false });
        }
      });

      deliveryTimeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((deliveryTime) => {
        const isDeliveryTime = deliveryTime > 0;

        immediatelyControl.setValue(!isDeliveryTime, { emitEvent: false });

        if (isDeliveryTime) {
          immediatelyControl.enable({ emitEvent: false });
        } else {
          immediatelyControl.disable({ emitEvent: false });
        }
      });

      if (immediatelyControl.value) {
        deliveryTimeControl.setValue(0);
      }
    });

    sendToAllAtOnceControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((isSendAtOnce) => {
      this.categoriesFormGroup.controls.forEach((categoryGroup) => {
        const immediatelyControl = categoryGroup.controls.immediately;
        const deliveryTimeControl = categoryGroup.controls.deliveryTime;
        const enabledControl = categoryGroup.controls.enabled;
        const branchesControl = categoryGroup.controls.branches;
        const categoryNameValue = categoryGroup.controls.name.value;

        const initBranches = this.getBranches(categoryNameValue);

        if (isSendAtOnce) {
          immediatelyControl.setValue(false, { emitEvent: false });
          immediatelyControl.disable({ emitEvent: false });

          deliveryTimeControl.setValue(0, { emitEvent: false });
          deliveryTimeControl.disable({ emitEvent: false });

          enabledControl.disable({ emitEvent: false });

          branchesControl.setValue(initBranches, { emitEvent: false });

          deliveryTimeToAllControl.enable({ emitEvent: false });
          deliveryTimeImmediateControl.enable({ emitEvent: false });

          enabledControl.setValue(branchesControl.value.length > 0, { emitEvent: false });
        } else {
          if (initBranches.length > 0) {
            deliveryTimeControl.enable({ emitEvent: false });
            deliveryTimeControl.setValue(deliveryTimeControl.value, { emitEvent: false });

            if (deliveryTimeControl.value === 0) {
              immediatelyControl.disable({ emitEvent: false });
            } else {
              immediatelyControl.enable({ emitEvent: false });
            }

            immediatelyControl.setValue(deliveryTimeControl.value === 0, { emitEvent: false });

            enabledControl.enable({ emitEvent: false });
          }

          deliveryTimeToAllControl.disable({ emitEvent: false });
          deliveryTimeImmediateControl.disable({ emitEvent: false });
        }
      });

      deliveryTimeImmediateControl.setValue(deliveryTimeToAllControl.value === 0, {
        emitEvent: false,
      });

      if (isSendAtOnce) {
        if (deliveryTimeToAllControl.value > 0) {
          deliveryTimeImmediateControl.enable({ emitEvent: false });
        } else {
          deliveryTimeImmediateControl.disable({ emitEvent: false });
        }
      } else {
        deliveryTimeToAllControl.setValue(0, { emitEvent: false });
        deliveryTimeImmediateControl.setValue(true, { emitEvent: false });
      }
    });

    deliveryTimeImmediateControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((isImmediately) => {
      if (isImmediately) {
        deliveryTimeToAllControl.setValue(0, { emitEvent: false });
        deliveryTimeImmediateControl.disable({ emitEvent: false });
      }
    });

    deliveryTimeToAllControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((deliveryTime) => {
      deliveryTimeImmediateControl.setValue(deliveryTime <= 0, { emitEvent: false });

      if (deliveryTime > 0) {
        deliveryTimeImmediateControl.enable({ emitEvent: false });
      } else {
        deliveryTimeImmediateControl.disable({ emitEvent: false });
      }
    });
  }
}
